import { useEffect, useState } from 'react';
import * as RRD from 'react-router-dom';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Collapse,
  Container,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  NavItem,
  NavLink,
} from 'reactstrap';

import SwedenFlag from 'assets/flags/sweden.svg';
import UKFlag from 'assets/flags/uk.svg';
import Logo from 'assets/flags/logo.svg';
import * as AuthenticationStore from 'store/redux/Authentication';
import { ApplicationState } from 'store/redux';
import { LoginState } from 'store/redux/Authentication';
import './NavMenu.css';

export type INavMenuProps = typeof AuthenticationStore.actions & {
  authenticationState?: AuthenticationStore.UserState;
};

const NavMenu = function ({ authenticationState, toggleUserProfileVisibility }: INavMenuProps) {
  const isLoggedIn = authenticationState
    ? authenticationState.LoginState === LoginState.LOGGED_IN
    : false;
  const customerHasBeenSelected =
    isLoggedIn &&
    !!authenticationState &&
    !!authenticationState.UserInRole &&
    !!authenticationState.UserInRole.selectedCustomer;
  const { t, i18n } = useTranslation('translation');
  const [isOpen, setIsOpen] = useState(false);
  const [showOrdersMenuItem, setShowOrdersMenuItem] = useState(false);
  const [showInvoicesMenuItem, setShowInvoicesMenuItem] = useState(false);
  const [showReportsMenuItem, setShowReportsMenuItem] = useState(false);
  const location = RRD.useLocation();
  const { pathname } = location;
  const splitLocation = pathname.split('/').filter((s) => s !== '');

  function PathStartsWith(actualPathTokens: string[], possibleMatchPathTokens: string[]) {
    if (possibleMatchPathTokens.length > actualPathTokens.length) {
      return false;
    }
    for (var i = 0; i < possibleMatchPathTokens.length; i++) {
      if (possibleMatchPathTokens[i] !== actualPathTokens[i]) {
        return false;
      }
    }
    return true;
  }

  useEffect(() => {
    if (isLoggedIn && !customerHasBeenSelected) {
      setShowOrdersMenuItem(false);
      setShowInvoicesMenuItem(false);
      setShowReportsMenuItem(false);
    } else {
      authenticationState?.UserInRole?.selectedCustomer &&
        setShowOrdersMenuItem(
          authenticationState?.UserInRole?.selectedCustomer?.orderServiceIsAvailable
        );
      authenticationState?.UserInRole?.selectedCustomer &&
        setShowInvoicesMenuItem(
          authenticationState?.UserInRole?.selectedCustomer?.invoicesAreAvailable
        );
      authenticationState?.UserInRole?.selectedCustomer &&
        setShowReportsMenuItem(
          authenticationState?.UserInRole?.selectedCustomer?.reportServiceIsAvailable
        );
    }
  }, [isLoggedIn, customerHasBeenSelected, authenticationState?.UserInRole?.selectedCustomer]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  let isActiveRoute = (possibleMatch: string) => {
    if (pathname === possibleMatch) {
      return true;
    }
    return PathStartsWith(
      splitLocation,
      possibleMatch.split('/').filter((s) => s !== '')
    );
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <header>
      <Navbar
        className={
          'navbar-expand-lg navbar-toggleable-lg border-bottom box-shadow mb-3' +
          (isLoggedIn ? ' logged-in' : ' not-logged-in')
        }
        light
      >
        <Container>
          <NavbarBrand tag={Link} to="/service">
            <img src={Logo} />
          </NavbarBrand>
          {!isLoggedIn && (
            <ul className="navbar-nav flex-grow top-nav-bar">
              <NavItem>
                <div className="language-selector-container">
                  <img
                    className="language-selector"
                    src={SwedenFlag}
                    onClick={() => {
                      changeLanguage('sv');
                    }}
                  />
                  <img
                    className="language-selector"
                    src={UKFlag}
                    onClick={() => {
                      changeLanguage('en');
                    }}
                  />
                </div>
              </NavItem>
            </ul>
          )}
          {isLoggedIn && (
            <>
              <NavbarToggler onClick={toggle} className="mr-2" />
              <Collapse className="d-lg-inline-flex flex-item flex-top " isOpen={isOpen} navbar>
                <ul className="navbar-nav flex-grow top-nav-bar">
                  <NavItem className={isActiveRoute('/service') ? 'active' : ''}>
                    <NavLink
                      onClick={() => {
                        setIsOpen(false);
                      }}
                      tag={RRD.NavLink}
                      className=""
                      to="/service"
                    >
                      {t('navigation.START')}
                    </NavLink>
                    <div className="active-expander" />
                    <Container className="submenu-container">
                      <ul className="navbar-nav flex-grow">
                        {showReportsMenuItem && (
                          <NavItem className={isActiveRoute('/service/statistics') ? 'active' : ''}>
                            <NavLink
                              onClick={() => {
                                setIsOpen(false);
                              }}
                              tag={RRD.NavLink}
                              to="/service/statistics"
                            >
                              {t('navigation.REPORT')}
                            </NavLink>
                          </NavItem>
                        )}
                        {showOrdersMenuItem && (
                          <NavItem className={isActiveRoute('/service/order') ? 'active' : ''}>
                            <NavLink
                              onClick={() => {
                                setIsOpen(false);
                              }}
                              tag={RRD.NavLink}
                              to="/service/order"
                            >
                              {t('navigation.ORDERS')}
                            </NavLink>
                          </NavItem>
                        )}
                        {showInvoicesMenuItem && (
                          <NavItem className={isActiveRoute('/service/invoices') ? 'active' : ''}>
                            <NavLink
                              onClick={() => {
                                setIsOpen(false);
                              }}
                              tag={RRD.NavLink}
                              to="/service/invoices"
                            >
                              {t('navigation.INVOICES')}
                            </NavLink>
                          </NavItem>
                        )}
                      </ul>
                    </Container>
                  </NavItem>
                  <NavItem className={isActiveRoute('/filearea') ? 'active' : ''}>
                    <NavLink
                      tag={RRD.NavLink}
                      onClick={() => {
                        setIsOpen(false);
                      }}
                      to="/filearea"
                    >
                      {t('navigation.FILEAREA')}
                    </NavLink>
                  </NavItem>
                  <NavItem className={isActiveRoute('/checklists') ? 'active' : ''}>
                    <NavLink
                      tag={RRD.NavLink}
                      onClick={() => {
                        setIsOpen(false);
                      }}
                      to="/checklists"
                    >
                      {t('navigation.CHECKLISTS')}
                    </NavLink>
                  </NavItem>
                  {!!authenticationState &&
                    !!authenticationState.UserInRole &&
                    !!authenticationState.UserInRole?.selectedCustomer && (
                      <NavItem className={isActiveRoute('/contact') ? 'active' : ''}>
                        <NavLink
                          tag={RRD.NavLink}
                          onClick={() => {
                            setIsOpen(false);
                          }}
                          to="/contact"
                        >
                          {t('navigation.CONTACT')}
                        </NavLink>
                        <div className="active-expander" />
                        <Container className="submenu-container">
                          <ul className="navbar-nav flex-grow">
                            <NavItem
                              className={isActiveRoute('/contact/conversation') ? 'active' : ''}
                            >
                              <NavLink tag={RRD.NavLink} to="/contact/conversation">
                                {t('navigation.CONVERSATIONS')}
                              </NavLink>
                            </NavItem>
                          </ul>
                        </Container>
                      </NavItem>
                    )}
                  <NavItem>
                    <div className="language-selector-container">
                      <img
                        className="language-selector"
                        src={SwedenFlag}
                        onClick={() => {
                          changeLanguage('sv');
                        }}
                      />
                      <img
                        className="language-selector"
                        src={UKFlag}
                        onClick={() => {
                          changeLanguage('en');
                        }}
                      />
                    </div>
                    {isLoggedIn && authenticationState && (
                      <div
                        title={
                          authenticationState.UserInRole
                            ? authenticationState.UserInRole.email
                            : authenticationState.UserName
                        }
                        className="login-persona-container text-right"
                      >
                        <div onClick={toggleUserProfileVisibility} id="profile" className="persona">
                          <i className="fa fa-user"></i>
                          {authenticationState.UserInRole
                            ? authenticationState.UserInRole.name
                            : authenticationState.UserName}
                          <br />
                          {authenticationState.UserInRole?.selectedCustomer &&
                            authenticationState.UserInRole.selectedCustomer.customerName}
                        </div>
                      </div>
                    )}
                  </NavItem>
                </ul>
              </Collapse>
            </>
          )}
        </Container>
      </Navbar>
    </header>
  );
};

export default connect(
  (appState: ApplicationState) => ({ authenticationState: appState.authentication }),
  AuthenticationStore.actions
)(NavMenu);
